/**
 * What the Calls tab reads off a call and off a failed request: the bounds
 * PlaceNimCall holds a brief and a reason to, the voice service's refusals
 * as the operator reads them, and a call's live duration.
 *
 * Refusals are read by their taxonomy reason (`parseError(e).code`), never
 * their message. The message is the voice service's product copy and is
 * shown as written; the hint is what this console adds about what to do.
 */

import { parseError } from '@liminal/web-ui/errors';
import { isHttpError } from '@liminal/errors/http-errors';
import type { LiminalErrorReason } from '@liminal/errors/liminal-errors';
import { formatOrderDate } from '@liminal/utils/date';
import { NimCall_State } from '@/api/generated';
import type { NimCall } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import { durationSeconds } from '@/lib/rewards';

/** The brief's bound in PlaceNimCall (admin_api nim.proto): what Nim is told, never shown to the user. */
export const BRIEF_MAX = 8000;
/** The reason's bound in PlaceNimCall: the phrase a user who misses the call reads after "about". */
export const REASON_MAX = 120;

/**
 * The voice service's refusals of a placed call (common/go/aip/errors,
 * LIM-4580). Each carries `satisfies`, so a reason renamed or dropped on the
 * backend fails the typecheck here rather than silently never matching.
 */
export const CALL_REFUSAL_REASON = {
  NOT_ALLOWED: 'VOICE_CALLS_NOT_ALLOWED' satisfies LiminalErrorReason,
  NO_RINGABLE_DEVICE: 'VOICE_CALL_NO_RINGABLE_DEVICE' satisfies LiminalErrorReason,
  QUIET_HOURS: 'VOICE_CALL_QUIET_HOURS' satisfies LiminalErrorReason,
  IN_PROGRESS: 'VOICE_CALL_IN_PROGRESS' satisfies LiminalErrorReason,
  DISABLED: 'VOICE_CALLS_DISABLED' satisfies LiminalErrorReason,
} as const;

type CallRefusalReason = (typeof CALL_REFUSAL_REASON)[keyof typeof CALL_REFUSAL_REASON];

const REFUSAL_REASONS: ReadonlySet<string> = new Set<string>(Object.values(CALL_REFUSAL_REASON));

function isRefusalReason(code: string): code is CallRefusalReason {
  return REFUSAL_REASONS.has(code);
}

/** What the operator can do about each refusal. Quiet hours add when they end. */
const REFUSAL_HINTS: Record<CallRefusalReason, string> = {
  [CALL_REFUSAL_REASON.NOT_ALLOWED]: 'Nim can call a user only once they turn on "Nim can call me" in the app.',
  [CALL_REFUSAL_REASON.NO_RINGABLE_DEVICE]:
    'Nim rings the Liminal app on an iPhone or an Android phone. They need to sign in to the app on one.',
  [CALL_REFUSAL_REASON.QUIET_HOURS]: 'Nim does not ring them inside the quiet hours they set.',
  [CALL_REFUSAL_REASON.IN_PROGRESS]: 'Place the call again once that one ends.',
  [CALL_REFUSAL_REASON.DISABLED]:
    'Calls with Nim (feature_nim_voice) or Nim calling them (feature_nim_calls_user) are off for this user.',
};

/** The metadata key VOICE_CALL_QUIET_HOURS carries (aiperrors.MetadataKeyQuietHoursEndTime). */
const QUIET_HOURS_END_TIME = 'quiet_hours_end_time';

/** The gRPC code the gateway's envelope carries for Unavailable. */
const GRPC_CODE_UNAVAILABLE = '14';

/** Why a call was not placed: the server's words, and what to do about it. */
export interface PlaceCallProblem {
  readonly message: string;
  readonly hint?: string;
}

/**
 * Whether the voice service could not be reached. Where it is not deployed
 * (prod, until its LiveKit project exists) every call answers this, so the
 * tab says calls are not available here rather than showing an error.
 */
export function isNimCallsUnavailable(error: unknown): boolean {
  return isHttpError(error) && error.data?.error?.code === GRPC_CODE_UNAVAILABLE;
}

/**
 * When the user's quiet hours end, as the RFC 3339 UTC time the refusal's
 * ErrorInfo detail carries, or undefined when it carries none.
 */
function quietHoursEndTime(error: unknown): string | undefined {
  if (!isHttpError(error)) return undefined;
  for (const detail of error.data?.error?.details ?? []) {
    if (typeof detail !== 'object' || detail === null) continue;
    if (!('reason' in detail) || detail.reason !== CALL_REFUSAL_REASON.QUIET_HOURS) continue;
    if (!('metadata' in detail)) continue;
    const metadata = detail.metadata;
    if (typeof metadata !== 'object' || metadata === null || !(QUIET_HOURS_END_TIME in metadata)) continue;
    const end = metadata[QUIET_HOURS_END_TIME];
    if (typeof end === 'string' && end !== '') return end;
  }
  return undefined;
}

/** Why PlaceNimCall failed, as the form shows it under its button. */
export function placeCallProblem(error: unknown): PlaceCallProblem {
  const parsed = parseError(error);

  if (isRefusalReason(parsed.code)) {
    if (parsed.code === CALL_REFUSAL_REASON.QUIET_HOURS) {
      const end = quietHoursEndTime(error);
      return {
        message: parsed.message,
        hint: end
          ? `Nim can call them again from ${formatOrderDate(end)} (your time).`
          : REFUSAL_HINTS[CALL_REFUSAL_REASON.QUIET_HOURS],
      };
    }
    return { message: parsed.message, hint: REFUSAL_HINTS[parsed.code] };
  }

  // The gate's other refusals ("Nim can't ring phones right now.") carry no
  // reason; their message is product copy all the same.
  if (parsed.category === 'BUSINESS') {
    return { message: parsed.message };
  }

  if (isNimCallsUnavailable(error)) {
    return {
      message: 'Nim could not reach the voice service.',
      hint: 'Try again: a retry places this same call, never a second one.',
    };
  }

  return { message: getErrorMessage(error) };
}

const RINGING_POLL_MS = 3_000;
const LIVE_POLL_MS = 15_000;

/**
 * How often the calls list refetches. A ringing call is answered, declined or
 * missed within the ring timeout (45 seconds), so a page with one polls
 * quickly; a live one gains its conversation when answered and its duration
 * when it ends, so a page with one polls slowly; otherwise it does not poll.
 */
export function nimCallsPollInterval(calls: readonly NimCall[]): number | false {
  if (calls.some((call) => call.state === NimCall_State.STATE_RINGING)) return RINGING_POLL_MS;
  if (calls.some((call) => call.state === NimCall_State.STATE_ANSWERED)) return LIVE_POLL_MS;
  return false;
}

/**
 * How long a call was live, as a phone's call log writes it ("1:35",
 * "1:02:07"), from its protobuf Duration ("95s"). A dash for a call that was
 * never answered or is still live, which has none.
 */
export function callDurationText(liveDuration: string | undefined): string {
  const total = durationSeconds(liveDuration);
  if (total === null) return '—';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = String(total % 60).padStart(2, '0');
  if (hours === 0) return `${minutes}:${seconds}`;
  return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`;
}
